import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import logo from "../assets/logo-dark.png";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);

  const handleSignup = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/newsletter/subscribe", { email, website });
      toast.success("You're on the list. Happy growing!");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-darkRoast text-cream mt-32" data-testid="main-footer">
      {/* Email signup banner */}
      <div className="border-b border-cream/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="text-xs tracking-wider-2 uppercase opacity-60 mb-1">Stay in the loop</div>
            <p className="font-display text-2xl">Planting tips & new varieties, straight to your inbox.</p>
          </div>
          <div className="flex w-full md:w-auto gap-2">
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSignup()}
              placeholder="your@email.com"
              className="flex-1 md:w-64 bg-cream/10 border border-cream/20 rounded-full px-5 py-3 text-sm text-cream placeholder:text-cream/40 focus:outline-none focus:border-sage"
            />
            <button
              onClick={handleSignup}
              disabled={loading}
              className="bg-sage text-cream rounded-full px-6 py-3 text-xs tracking-wider-2 uppercase hover:bg-cream hover:text-darkRoast transition-colors disabled:opacity-50"
            >
              {loading ? "…" : "Subscribe"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-5 gap-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-6">
            <img
              src={logo}
              alt="Rooted in Amador, Natural Seed Shop logo"
              className="w-16 h-16 object-contain"
              data-testid="footer-logo-img"
            />
            <div>
              <div className="font-display text-2xl">Rooted in Amador</div>
              <div className="text-[10px] tracking-wider-2 uppercase opacity-70">Natural Seed Shop</div>
            </div>
          </div>
          <p className="font-display text-2xl leading-snug max-w-md opacity-90">
            Naturally sourced, non-GMO seeds, packed by hand in the rolling hills of California's
            Amador County.
          </p>
        </div>

        <div>
          <h4 className="text-xs tracking-wider-2 uppercase opacity-60 mb-4">Shop</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/shop" className="hover:text-sage">All Seeds</Link></li>
            <li><Link to="/grow" className="hover:text-sage">How to Grow</Link></li>
            <li><Link to="/shop?category=Vegetables" className="hover:text-sage">Vegetables</Link></li>
            <li><Link to="/shop?category=Herbs" className="hover:text-sage">Herbs</Link></li>
            <li><Link to="/shop?category=Flowers" className="hover:text-sage">Flowers</Link></li>
          </ul>
        </div>
        <div>
         <h4 className="text-xs tracking-wider-2 uppercase opacity-60 mb-4">Policies</h4>
         <ul className="space-y-2 text-sm">
          <li><Link to="/privacy" className="hover:text-sage opacity-90">Privacy Policy</Link></li>
          <li><Link to="/terms" className="hover:text-sage opacity-90">Terms of Service</Link></li>
          <li><Link to="/shipping" className="hover:text-sage">Shipping & Pickup</Link></li>
          <li><Link to="/returns" className="hover:text-sage">Returns</Link></li>
        </ul>
       </div>
        <div>
          <h4 className="text-xs tracking-wider-2 uppercase opacity-60 mb-4">Connect</h4>
          <ul className="space-y-3 text-sm">
          <li className="flex items-center gap-2 opacity-90"><MapPin className="w-4 h-4" strokeWidth={1.5} /> Plymouth, CA</li>
          <li className="flex items-center gap-2 opacity-90"><Mail className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} /><a href="mailto:hello@rootedinamador.com" className="hover:text-sage">hello@rootedinamador.com</a></li>
          <li className="flex items-center gap-2 opacity-90"><Phone className="w-4 h-4" strokeWidth={1.5} /><a href="tel:+12094183142" className="hover:text-sage">(209) 418-3142</a></li>
          <li className="flex items-center gap-2 opacity-90"><Send className="w-4 h-4" strokeWidth={1.5} /><Link to="/contact" className="hover:text-sage">Contact Form</Link></li>
          </ul>
        </div>
      </div>



      <div className="border-t border-cream/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between text-xs opacity-70">
          <span>© {new Date().getFullYear()} Rooted in Amador. All rights reserved.</span>
          <span className="tracking-wider-2 uppercase">Naturally Sourced · Non-GMO · Hand Packed</span>
        </div>
      </div>
    </footer>
  );
}
